import { useRouter } from 'next/router'
import { useCallback, useEffect, useReducer, useState, VFC } from 'react'
import { useInView } from 'react-intersection-observer'
import { ROOMS_PAGE_SIZE, useRooms } from 'src/api/room'
import { ALERT_BANNER_HEIGHT } from 'src/components/Banner/common'
import { FixedSalesDemoAlertBanner } from 'src/components/Banner/SalesDemoAlertBanner'
import { MemberHeader, MemberHeaderProps } from 'src/components/Header'
import { NARROW_HEADER_HEIGHT } from 'src/components/Header/NarrowHeader/MemberNarrowHeader'
import { HEADER_HEIGHT } from 'src/components/Header/WideHeader/HeaderWrapper'
import {
  CreationSourceType,
  MATCHING_STATUSES_STR,
  MatchingStatusType,
  RoomStatusType,
} from 'src/models/Room'
import { companyDetail } from 'src/utils/routes'
import {
  convertMessagePageQuqry,
  convertTagIds,
  messagePageQueryType,
  queryType,
} from 'src/utils/routes/utils'
import styled, { css } from 'styled-components'
import { FreeCompanyAlertBanner } from '../../components/Banner/FreeCompanyAlertBanner'
import { MessageComponent } from './Message'
import { useMessageContext } from './MessageContext'
import { Modals } from './Modals'
import { RoomsList } from './RoomsList'
import { SideMenuAndRoomList } from './SideMenuAndRoomList'
import { SubMenu } from './SubMenu'
import { DEFAULT_TAB_TYPE, TabType } from './SubMenu/TabList'

type MessagesLayoutProps = MemberHeaderProps & {}
export const MessagesLayout: VFC<MessagesLayoutProps> = ({ user }) => {
  const [isSubMenuOpen, toggleSubMenu] = useReducer((prev) => !prev, false)
  const [tabType, setTabType] = useState<TabType>(DEFAULT_TAB_TYPE)
  const {
    room,
    setReportModalOpen,
    viewingUserId,
    setViewingUserId,
    mutateTags,
    mutate: mutateRoom,
  } = useMessageContext()

  const { query: _query } = useRouter()
  const query = convertMessagePageQuqry(_query)
  const [roomName, setRoomName] = useState<string>(
    typeof query.room_name === 'string' ? query.room_name : '',
  )
  const [matchingStatus, setMatchingStatus] = useState<MatchingStatusType>(
    convertMatchingStatus(query.matching_status),
  )
  const [creationSource, setCreationSource] = useState<CreationSourceType>(
    convertCreationSource(query),
  )
  const [roomStatus, setRoomStatus] = useState<RoomStatusType>(
    convertRoomStatus(query),
  )
  const [tagIds, setSearchTagIds] = useState<number[]>(
    convertTagIds(query.tag_ids),
  )

  const {
    data: roomsArr,
    mutate: mutateRooms,
    isLoadingInitialData,
    fetchMore,
  } = useRooms(
    {
      query: {
        roomName: roomName ? roomName : undefined,
        visibilityStatus: roomStatus === 'hidden' ? 'hidden' : undefined,
        unreadOnly: roomStatus === 'unread_only' ? true : undefined,
        hasMemo: roomStatus === 'has_memo' ? true : undefined,
        matchingStatus: matchingStatus ? matchingStatus : undefined,
        ...creationSource,
        tagIds: tagIds,
        roomType:
          roomStatus === 'company_message_room' ||
          roomStatus === 'company_message_group_room'
            ? roomStatus
            : undefined,
      },
      userId: user.id,
    },
    ROOMS_PAGE_SIZE,
  )

  const { ref, inView } = useInView({})
  const FecthMoreTrigger = useCallback(() => <div ref={ref} />, [ref])

  // レンダリング時に query の値が存在する or 更新されたらセットする
  useEffect(() => {
    if (typeof query.room_name === 'string')
      setRoomName(String(query.room_name))
  }, [query.room_name])
  useEffect(() => {
    setMatchingStatus(convertMatchingStatus(query.matching_status))
  }, [query.matching_status])
  useEffect(() => {
    setCreationSource(convertCreationSource(query))
  }, [
    query.sent_request_from_search,
    query.recieved_request_from_search,
    query.sent_request_from_request_box,
    query.recieved_request_from_request_box,
    query.sent_request_from_contribution,
    query.recieved_request_from_contribution,
    query.creation_source_is_referrer,
    query.creation_source_is_event,
    query.requested_from_other,
  ])
  useEffect(() => {
    setRoomStatus(convertRoomStatus(query))
  }, [
    query.has_memo,
    query.unread_only,
    query.visibility_status,
    query.room_type,
  ])
  useEffect(() => {
    setSearchTagIds(convertTagIds(query.tag_ids))
  }, [query.tag_ids])

  useEffect(() => {
    if (fetchMore && inView) fetchMore()
  }, [fetchMore, inView])

  const mutates = () => {
    mutateTags && mutateTags()
    mutateRoom && mutateRoom()
    mutateRooms()
  }

  // NOTE: 詳細ボタンを押したらユーザー詳細画面を開く(Messages/Modals/index.tsxには含めない)
  useEffect(() => {
    if (viewingUserId !== undefined) {
      window.open(companyDetail(viewingUserId, false, false), '_blank')
      setViewingUserId(undefined), [viewingUserId, setViewingUserId]
    }
  })

  const isDisplayBanner = user.plan.freeAndPartner || user.plan.salesDemo

  return (
    <>
      <MemberHeader user={user} />
      {user.plan.freeAndPartner ? (
        <FreeCompanyAlertBanner />
      ) : user.plan.salesDemo ? (
        <FixedSalesDemoAlertBanner
          salesDemoDaysLeftText={user.salesDemoDaysLeftText}
        />
      ) : (
        <></>
      )}
      <Main isDisplayBanner={isDisplayBanner} inRoomList={!room}>
        <SideMenuAndRoomList
          user={user}
          roomsArr={roomsArr}
          mutateRooms={mutateRooms}
          isLoadingInitialData={isLoadingInitialData}
          FecthMoreTrigger={FecthMoreTrigger}
          roomName={roomName}
          matchingStatus={matchingStatus}
          creationSource={creationSource}
          roomStatus={roomStatus}
        />
        <MessageComponent
          user={user}
          userId={user.id}
          showMessageAttention={user.settings.showMessageAttention}
          myScheduleUrl={user.company.scheduleUrl}
          isActiveScheduleUrl={user.company.isActiveScheduleUrl}
          scheduleUrlBannerLastClosedAt={
            user.company.scheduleUrlBannerLastClosedAt
          }
          toggleSubMenu={toggleSubMenu}
          openReportUserModal={() => setReportModalOpen(true)}
          mutateRooms={mutateRooms}
          isSubMenuOpen={isSubMenuOpen}
          activeTab={tabType}
          setTabType={setTabType}
          paid={user.plan.paid || user.plan.isAdminRole}
          showProfile={setViewingUserId}
        />
        <SubMenu
          user={user}
          isSubMenuOpen={isSubMenuOpen}
          showProfile={setViewingUserId}
          activeTab={tabType}
          setTabType={setTabType}
          toggleSubMenu={toggleSubMenu}
        />
      </Main>
      <Modals mutates={mutates} />
    </>
  )
}

const convertToBool = (value: queryType): boolean => {
  if (typeof value === 'string' && value === 'true') return true
  return false
}
const convertMatchingStatus = (value: queryType): MatchingStatusType => {
  if (typeof value === 'string' && MATCHING_STATUSES_STR.includes(value))
    return value as MatchingStatusType
  return 'all_room'
}
const convertCreationSource = (
  value: messagePageQueryType,
): CreationSourceType => {
  const DEFAULT_VALUE = true // NOTE: 初期状態は checkbox: true にするため
  return {
    sent_request_from_search: value.sent_request_from_search
      ? convertToBool(value.sent_request_from_search)
      : DEFAULT_VALUE,
    recieved_request_from_search: value.recieved_request_from_search
      ? convertToBool(value.recieved_request_from_search)
      : DEFAULT_VALUE,
    sent_request_from_request_box: value.sent_request_from_request_box
      ? convertToBool(value.sent_request_from_request_box)
      : DEFAULT_VALUE,
    recieved_request_from_request_box: value.recieved_request_from_request_box
      ? convertToBool(value.recieved_request_from_request_box)
      : DEFAULT_VALUE,
    sent_request_from_contribution: value.sent_request_from_contribution
      ? convertToBool(value.sent_request_from_contribution)
      : DEFAULT_VALUE,
    recieved_request_from_contribution: value.recieved_request_from_contribution
      ? convertToBool(value.recieved_request_from_contribution)
      : DEFAULT_VALUE,
    creation_source_is_referrer: value.creation_source_is_referrer
      ? convertToBool(value.creation_source_is_referrer)
      : DEFAULT_VALUE,
    creation_source_is_event: value.creation_source_is_event
      ? convertToBool(value.creation_source_is_event)
      : DEFAULT_VALUE,
    requested_from_other: value.requested_from_other
      ? convertToBool(value.requested_from_other)
      : DEFAULT_VALUE,
  }
}
const convertRoomStatus = (value: messagePageQueryType): RoomStatusType => {
  if (typeof value.has_memo === 'string' && value.has_memo === 'true')
    return 'has_memo'
  else if (
    typeof value.unread_only === 'string' &&
    value.unread_only === 'true'
  )
    return 'unread_only'
  else if (
    typeof value.visibility_status === 'string' &&
    value.visibility_status === 'hidden'
  )
    return 'hidden'
  else if (
    typeof value.room_type === 'string' &&
    value.room_type === 'company_message_room'
  )
    return 'company_message_room'
  else if (
    typeof value.room_type === 'string' &&
    value.room_type === 'company_message_group_room'
  )
    return 'company_message_group_room'
  return 'unspecified'
}

const Main = styled.main<{ isDisplayBanner: boolean; inRoomList: boolean }>`
  display: flex;
  flex-direction: row;
  height: 100%;

  ${({ isDisplayBanner }) =>
    isDisplayBanner
      ? css`
          padding-top: calc(${HEADER_HEIGHT} + ${ALERT_BANNER_HEIGHT});
        `
      : css`
          padding-top: calc(${HEADER_HEIGHT});
        `}
  @media screen and (max-width: 960px) {
    padding-top: ${NARROW_HEADER_HEIGHT};
  }

  ${RoomsList} {
    min-width: 280px;
    max-width: 376px;
    flex: 2;

    @media screen and (max-width: 960px) {
      max-width: 100%;
    }
  }
  ${MessageComponent} {
    flex: 3;

    @media screen and (max-width: 960px) {
      ${({ inRoomList }) => inRoomList && 'display: none;'}
    }
  }
  ${SubMenu} {
    flex: 2;
    max-width: 334px;
  }
`
